import * as React from 'react';
import clsx from 'clsx';
import i18next from 'i18next';
import { WithTranslation, withTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { IObjectRouteParams, routes } from 'routes';
import { connect } from 'react-redux';
import {
    Panel,
    PanelHeader,
    PanelBody,
    PanelFooter,
    Button,
    Icons,
    ButtonGroup,
    CloseButton,
    LinkButton,
    LinkWindow,
    Preloader
} from 'components';
import { IState } from 'services/root-reducer';
import { IOperation, EOperationStatus, IAppConfig, IObjectSignature } from 'types';
import {
    BaseLayerCode, ComparableLayers, favouritesContainersCodes, pdfContainerCodes
} from 'project-constants';
import {
    getOperationSafe,
    getOmjsLayerConfig,
    getOperation,
    getLayerIcon,
    getTopContainer,
    getFavorites, getOmjsContainer, getOperationData
} from 'selectors';
import { actions as operationsActions } from 'services/operations/operations-service';
import { actions as mapActions } from 'services/map/map-service';
import { actions as favoritesActions } from 'services/favorites/favorites-service';
import { actions as overlayWindowsActions } from 'services/overlay-windows/overlay-windows-service';
import { actions as containersActions } from 'services/containers/containers-service';
import { ObjectWindowTab } from 'scenes/object-window';
import { SlideUpPanel } from 'mobile-ui/components/slideup-panel';
import { ObjectsHelper, formatApiUrl } from 'utils';
import {
    IShowObjectWindowOperationData,
    actions as objectWindowActions
} from 'services/object-window/object-window-service';
import { IContainer } from 'types/omjs/project';
import { CSSTransition } from 'react-transition-group';
import { ObjectMedia } from 'scenes/object-window/object-media';
import { MobileUIObjectInfo, MobileUIObjectInfoComponent } from './object-info';
import './style.scss';


export interface IMobileUIObjectWindowProps extends RouteComponentProps<IObjectRouteParams>, WithTranslation {
    showObjectOperation: IOperation<IShowObjectWindowOperationData>;
    apiUrlWithVersion: string;
    config: IAppConfig;
    favorites: IState[ 'favorites' ][ 'items' ];
    isInFavorites: boolean;
    onZoomToObject: (sig: IObjectSignature) => void;
    containers: IContainer[]
}
export interface IMobileUIObjectWindowState {
    activeTab: ObjectWindowTab;
    minimized: boolean;
    /**
     * по задаче @xa - "Адаптив. Оглавление глючит. Лучше вообще его убрать"
     * код удалять не стал, но закомментировал
     */
    // tocVisible: boolean;
}

const mapStateToProps = (state: IState) => {
    let operation: IMobileUIObjectWindowProps['showObjectOperation'] = getOperation(state, 'SHOW_OBJECT_WINDOW');
    let operation_success = operation?.status === EOperationStatus.SUCCESS;
    let { config } = state.app;
    let favorites = getFavorites(state);
    let in_favorites = operation_success
        ? (favorites[operation.data.layer_code]?.includes(operation.data.object.id) ?? false)
        : false;
    return {
        showObjectOperation: operation,
        apiUrlWithVersion: formatApiUrl(config.apiUrl),
        config: config,
        favorites: getFavorites(state),
        isInFavorites: in_favorites,
        containers: getOperationData(state, 'CONTAINERS')
    };
};

class MobileUIObjectWindowComponent extends React.Component<IMobileUIObjectWindowProps, IMobileUIObjectWindowState> {
    private MobileUIObjectInfo: MobileUIObjectInfoComponent;

    constructor(props: IMobileUIObjectWindowProps) {
        super(props);

        this.state = this.getInitialState();

        this.onCloseClick = this.onCloseClick.bind(this);
        this.onZoomToObjectClick = this.onZoomToObjectClick.bind(this);
        this.onLinkClick = this.onLinkClick.bind(this);
        this.onPDFClick = this.onPDFClick.bind(this);
        this.onFavoritesClick = this.onFavoritesClick.bind(this);
        this.onDataUpdateClick = this.onDataUpdateClick.bind(this);
        this.loadObjectData = this.loadObjectData.bind(this);
    }

    getInitialState(): IMobileUIObjectWindowState {
        return {
            activeTab: ObjectWindowTab.Info,
            minimized: false
        };
    }

    componentDidMount() {
        let { layerCode, objectId } = this.props.match.params;

        this.setTitle();

        if (!ComparableLayers.has(layerCode)) {
            mapActions.getConnections([ 0, 0 ]);
        } else {
            mapActions.getConnectionsByObject({
                layerCode: layerCode,
                objectId: parseInt(objectId)
            });
        }
        if (i18next.language !== 'ru' && [
            'oks_po_dannym_kpt',
            'zu_po_dannym_kpt',
            'investment_projects',
            'productive_agricultural_land' ].includes(layerCode)) {
            containersActions.setContainerSelected({ id_or_code: layerCode, selected: false });
            routes.goBack(routes.map, { container: 'root' }, routes.currRouteState);
        } else {
            this.loadObjectData();
        }
    }

    componentDidUpdate(prevProps: Readonly<IMobileUIObjectWindowProps>) {
        const { match, showObjectOperation } = this.props;
        let { layerCode, objectId } = match.params;
        let { layerCode: prevLayerCode, objectId: prevObjectId } = prevProps.match.params;

        this.setTitle();

        if (!ComparableLayers.has(layerCode)) {
            mapActions.getConnections([ 0, 0 ]);
        }

        if (layerCode !== prevLayerCode || objectId !== prevObjectId) {
            /* eslint-disable-next-line */
            this.setState(this.getInitialState());

            if (ComparableLayers.has(layerCode)) {
                mapActions.getConnectionsByObject({
                    layerCode: layerCode,
                    objectId: parseInt(objectId)
                });
            }

            objectWindowActions.showObjectWindow({
                object_id: parseInt(objectId),
                layer_code: layerCode
            });
        }
        if (this.props.showObjectOperation?.status !== prevProps.showObjectOperation?.status
            && this.props.showObjectOperation?.status === EOperationStatus.SUCCESS) {
            const { geom, fullGeom } = this.props.showObjectOperation.data.object;
            const g = (fullGeom || geom);
            if (g) {
                mapActions.setObjectHighlight(g);
            }
        }
    }

    componentWillUnmount() {
        mapActions.setObjectHighlight(null);
    }

    setTitle() {
        let { showObjectOperation } = this.props;
        if (showObjectOperation && showObjectOperation.status === EOperationStatus.SUCCESS) {
            let layerConfig = getOmjsLayerConfig(false, showObjectOperation.data.layer_code) as omjs.ILayerConfig;
            let { object } = showObjectOperation.data;
            let title = ObjectsHelper.getObjectName(object, layerConfig);
            title = title.replace(/&quot;/ig, '"');
            document.title = `${title}. ${i18next.t('Ленинградская область')}`;
        }
    }

    onPDFClick() {
        let {
            apiUrlWithVersion,
            config,
            i18n,
            showObjectOperation
        } = this.props;

        let topContainer = getTopContainer(false, showObjectOperation.data.layer_code) as omjs.Container;
        let { object, layer_code } = showObjectOperation.data;

        // код оригинального слоя, по которому созданы виртуальные слои категорий
        let baseLayerCode = BaseLayerCode[topContainer.get('code')] || layer_code;

        let path = `${apiUrlWithVersion + config.mapCode}/layers/${baseLayerCode}/objects/${object.id}/`;
        let query = `f=pdf&custom=1&lng=${i18n.language}&dl=1`;

        window.open(`${path}?${query}`);
    }

    onLinkClick() {
        let { showObjectOperation, i18n } = this.props;
        let { layer_code, object } = showObjectOperation.data;

        let object_link = ObjectsHelper.getObjectLink({
            layer_code,
            object_id: object.id
        });

        overlayWindowsActions.showObjectLinkOverlay(object_link);
    }

    onCloseClick(event: React.MouseEvent) {
        let { showObjectOperation } = this.props;
        let { layer_code } = showObjectOperation.data;
        operationsActions.clearOperation('SHOW_OBJECT_WINDOW');
        mapActions.getConnections([ 0, 0 ]);
        const { navigation } = routes;
        let removeNavItem = false;

        if (navigation.length) {
            removeNavItem = navigation[navigation.length - 1].params.objectId !== undefined;
            if (navigation[navigation.length - 1].route.path.indexOf('/list/') === 0) {
                let listLayerCode = navigation[navigation.length - 1].params.layerCode;
                let topContainerCode = getTopContainer(false, layer_code).get('code');
                removeNavItem = (
                    removeNavItem || (layer_code !== listLayerCode && topContainerCode !== listLayerCode)
                );
            }
        }

        while (removeNavItem) {
            routes.navigation.pop();
            if (navigation.length) {
                removeNavItem = navigation[navigation.length - 1].params.objectId !== undefined;
                if (navigation[navigation.length - 1].route.path.indexOf('/list/') === 0) {
                    let listLayerCode = navigation[navigation.length - 1].params.layerCode;
                    let topContainerCode = getTopContainer(false, layer_code).get('code');
                    removeNavItem = (
                        removeNavItem || (layer_code !== listLayerCode && topContainerCode !== listLayerCode)
                    );
                }
            } else {
                removeNavItem = false;
            }
        }
        routes.goBack(routes.map, { container: 'root' }, routes.currRouteState);
    }

    onZoomToObjectClick() {
        let { object, layer_code } = this.props.showObjectOperation.data;
        this.props.onZoomToObject({ layer_code, object_id: object.id });
        // routes.redirect(routes.map, {
        //     container: 'root'
        // });
        const cont = getOmjsContainer(false, layer_code, true);
        if (cont.get('render_type') === 0) {
            const objGeom = object['geom'];
            if (objGeom) {
                const geom = new omjs.ol.geom[objGeom.type](objGeom.coordinates);
                mapActions.fitToExtent(geom.getExtent());
            }
        } else {
            mapActions.fitToTarget({
                layer: layer_code,
                objectId: object.id
            });
        }
        this.setState({
            minimized: true
        });
    }

    onFavoritesClick() {
        let { isInFavorites } = this.props;
        let { object, layer_code } = this.props.showObjectOperation.data;
        if (isInFavorites) {
            favoritesActions.removeItem({ layer_code, object_id: object.id });
        } else {
            favoritesActions.addItem({ layer_code, object_id: object.id });
        }
    }

    onDataUpdateClick() {
        let { showObjectOperation } = this.props;
        let { object, layer_code } = this.props.showObjectOperation.data;

        let layerConfig = getOmjsLayerConfig(false, showObjectOperation.data.layer_code) as omjs.ILayerConfig;

        overlayWindowsActions.showUpdateDataOverlay({
            layer_code,
            object_id: object.id,
            object_name: ObjectsHelper.getObjectName(object, layerConfig),
            object_url: ObjectsHelper.getObjectLink({
                layer_code,
                object_id: object.id
            })
        });
    }

    loadObjectData() {
        let { layerCode, objectId } = this.props.match.params;
        objectWindowActions.showObjectWindow({
            object_id: parseInt(objectId),
            layer_code: layerCode
        });
    }

    renderPreloader() {
        return (
            <PanelBody className='window-body'>
                <div className="preloader-wrapper">
                    <Preloader />
                </div>
            </PanelBody>
        );
    }

    renderError() {
        let { t } = this.props;
        return (
            <React.Fragment>
                <PanelHeader className='window-header'>
                    <div className="tabs-switcher"></div>
                    <div className="close-button-wrapper">
                        <CloseButton onClick={this.onCloseClick} />
                    </div>
                </PanelHeader>

                <PanelBody className='error window-body'>
                    <div className='error-message'>
                        {t('Ошибка загрузки')}
                    </div>
                    <div>
                        <LinkButton
                            className='retry-button'
                            onClick={this.loadObjectData}
                            label={t('Повторить')}
                        />
                    </div>
                </PanelBody>
            </React.Fragment>
        );
    }

    renderPanelHeader(hasMedia: boolean) {
        let { layerCode, objectId } = this.props.match.params;
        if (hasMedia) {
            return (
                <PanelHeader className='window-header'>
                    <div className="tabs-switcher">
                        <LinkButton
                            icon={<Icons.IconInfo />}
                            active={this.state.activeTab === ObjectWindowTab.Info}
                            onClick={() => {
                                this.setState({
                                    activeTab: ObjectWindowTab.Info
                                    // tocVisible: false
                                });
                            }}
                        />
                        <LinkButton
                            icon={layerCode !== 'investment_projects'
                                ? <Icons.IconCamera /> : <Icons.IconDoc />}
                            active={this.state.activeTab === ObjectWindowTab.Gallery}
                            onClick={() => {
                                this.setState({
                                    activeTab: ObjectWindowTab.Gallery
                                    // tocVisible: false
                                });
                            }}
                        />
                    </div>
                    <div className="close-button-wrapper">
                        <CloseButton onClick={this.onCloseClick} />
                    </div>
                </PanelHeader>
            );
        } else {
            return null;
        }
    }

    renderPanel() {
        let {
            showObjectOperation,
            isInFavorites,
            containers,
            t
        } = this.props;

        if (
            !showObjectOperation
            || showObjectOperation.status === EOperationStatus.PENDING
            || showObjectOperation.status === EOperationStatus.UNKNOWN
        ) {
            return this.renderPreloader();
        } else if (showObjectOperation.status === EOperationStatus.FAIL) {
            return this.renderError();
        }

        let layerConfig = getOmjsLayerConfig(false, showObjectOperation.data.layer_code) as omjs.ILayerConfig;

        let { object, structure, filtered_structure } = showObjectOperation.data;
        let image_column_code = ObjectsHelper.getImagesColumnCode(object, layerConfig);
        let files_column_code = ObjectsHelper.getFilesColumnCode(object, structure, layerConfig);
        let file_columns = ObjectsHelper.getFilesColumnCodes(object, structure, layerConfig);
        let has_media = !!image_column_code || file_columns.length > 0;
        const withBanner = [ 'parks_3', 'parks_2', 'parks_1', 'sites_1', 'sites_2', 'sites_3' ]
            .includes(showObjectOperation?.data?.layer_code);
        const withConnections = [ 'sites_1', 'sites_2', 'sites_3' ].includes(showObjectOperation?.data?.layer_code);

        return (
            <React.Fragment>
                { !this.state.minimized && this.renderPanelHeader(has_media) }

                <div className="layer-icon-name-wrapper">
                    <div className="layer-icon">
                        <img
                            src={getLayerIcon(false, {
                                layer_code: showObjectOperation.data.layer_code,
                                object_id: showObjectOperation.data.object.id
                            })}
                        />
                    </div>
                    <div
                        className="layer-name"
                        dangerouslySetInnerHTML={{
                            __html: ObjectsHelper.getObjectName(object, layerConfig)
                        }}
                        onClick={() => this.setState({ minimized: false })}
                    />
                    {
                        (!has_media || this.state.minimized) && (
                            <div className="close-button-wrapper">
                                <CloseButton onClick={this.onCloseClick} />
                            </div>
                        )
                    }
                </div>

                {/* <div
                    className={clsx('toc-button-wrapper', {
                        'toc-visible': this.state.tocVisible
                    })}
                >
                    <TOCToggleButton
                        disabled={this.state.tocVisible}
                        onClick={() => {
                            this.setState(state => ({
                                tocVisible: !state.tocVisible,
                                activeTab: ObjectWindowTab.Info
                            }));
                        }}
                    />
                </div> */}

                <PanelBody className='window-body'>
                    <CSSTransition
                        in={/* this.state.tocVisible || */ this.state.activeTab === ObjectWindowTab.Gallery}
                        classNames="animation-state"
                        timeout={200}
                    >
                        <MobileUIObjectInfo
                            object={object}
                            className='slide-right-animation'
                            structure={filtered_structure}
                            layerConfig={layerConfig}
                            withBanner={withBanner}
                            withConnections={withConnections}
                            ref={(c) => {
                                this.MobileUIObjectInfo = c;
                            }}
                            containers={containers}
                            t={t}
                        />
                    </CSSTransition>

                    {/* <CSSTransition
                        // in={this.state.tocVisible}
                        classNames="animation-state"
                        timeout={200}
                    >
                        <ObjectToc
                            object={object}
                            structure={filtered_structure}
                            className='slide-left-animation'
                            layerConfig={layerConfig}
                            onHeaderClick={(column) => {
                                this.setState({
                                    tocVisible: false
                                });
                                this.MobileUIObjectInfo.scrollToColumn(column);
                            }}
                        />
                    </CSSTransition> */}

                    <CSSTransition
                        in={this.state.activeTab === ObjectWindowTab.Gallery}
                        classNames="animation-state"
                        timeout={200}
                    >
                        <ObjectMedia
                            object={object}
                            className='slide-left-animation'
                            filesColumns={file_columns}
                            imagesColumn={image_column_code}
                            layerConfig={layerConfig}
                            layerCode={showObjectOperation.data.layer_code}
                        />
                    </CSSTransition>
                </PanelBody>

                <PanelFooter className='object-window-footer'>
                    <ButtonGroup>
                        {object['geom'] && (
                            <Button
                                variant='blue'
                                onClick={this.onZoomToObjectClick}
                                icon={<Icons.IconMagnifyGlassPlus />}
                            />
                        )}
                        {
                            !__IS_CORDOVA__ && (
                                <Button
                                    variant='blue'
                                    onClick={this.onLinkClick}
                                    icon={<Icons.IconLink />}
                                />
                            )
                        }
                        {pdfContainerCodes.includes(showObjectOperation.data.layer_code) && (
                            <Button
                                variant='blue'
                                onClick={this.onPDFClick}
                                icon={<Icons.IconPdf />}
                            />
                        )}
                        {favouritesContainersCodes.includes(showObjectOperation.data.layer_code) && (
                            <Button
                                variant="blue"
                                onClick={this.onFavoritesClick}
                                className={clsx({
                                    'in-favorites-icon-color': isInFavorites
                                })}
                                icon={<Icons.IconStar />}
                            />
                        )}
                        {/* <Button
                            title="Заявка на обновление данных"
                            variant="blue"
                            className='data-update-button'
                            onClick={this.onDataUpdateClick}
                            icon={<Icons.IconNote />}
                        /> */}
                    </ButtonGroup>
                </PanelFooter>
            </React.Fragment>
        );
    }

    render() {
        return (
            <SlideUpPanel open className={clsx('mobile-ui-object-window', this.state.minimized && 'minimized')}>
                { this.renderPanel() }
            </SlideUpPanel>
        );
    }
}

export const MobileUIObjectWindow = withRouter(
    // @ts-ignore
    withTranslation()(
        connect(mapStateToProps)(MobileUIObjectWindowComponent)
    )
);
